import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, AlertIcon, Box, Button, CloseButton, HStack, Text, VStack,
  Badge, Collapse, useToast, Spinner, useColorModeValue,
} from '@chakra-ui/react';
import { FiLink, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { getAriveStageLabel, getAriveStageColor } from '../../../constants/ariveConstants';
import { useAriveCredentials, useAriveAccess } from '../../../hooks/useArive';
import { useAuth } from '../../../contexts/AuthContext';

export default function AriveAutoSuggestBanner({ deal, onLinked }) {
  // All hooks must be called unconditionally before any early return
  // (see .cursor/rules/react-hooks-no-conditional-calls.mdc).
  const { currentUser } = useAuth();
  const { hasCredentials, isEnabled, loading: credsLoading } = useAriveCredentials(currentUser?.uid);
  const { companySyncConfigured, loading: accessLoading } = useAriveAccess();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [linking, setLinking] = useState(null);
  const toast = useToast();

  const rowBg = useColorModeValue('white', 'gray.700');
  const rowBorder = useColorModeValue('gray.100', 'gray.600');

  const canUseArive = companySyncConfigured || (hasCredentials && isEnabled);

  const fetchSuggestions = useCallback(async () => {
    // Skip the API call when neither company sync nor personal credentials
    // are configured -- the backend would just fail at client resolution.
    if (!canUseArive) return;
    if (!deal?.id || deal.ariveId) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'suggestAriveLinks');
      const result = await fn({ dealId: deal.id });
      setMatches(result.data?.matches || []);
    } catch (_) {
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [deal?.id, deal?.ariveId, canUseArive]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleLink = async (ariveLoanId) => {
    if (!isSupabaseConfigured()) return;
    setLinking(ariveLoanId);
    try {
      const { error } = await supabase
        .from('crm_deals')
        .update({ arive_id: ariveLoanId, updated_at: new Date().toISOString() })
        .eq('id', deal.id);
      if (error) throw error;

      // Pull fresh Arive data into the deal
      try {
        const refreshFn = httpsCallable(functionsUsEast1, 'refreshAriveData');
        await refreshFn({ dealId: deal.id });
      } catch (_) {
        // Non-critical -- the data will sync on next manual refresh
      }

      toast({ title: 'Deal linked to Arive', description: `Linked to ${ariveLoanId}`, status: 'success', duration: 3000 });
      setMatches([]);
      setDismissed(true);
      if (onLinked) onLinked(ariveLoanId);
    } catch (err) {
      toast({ title: 'Error linking deal', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLinking(null);
    }
  };

  // Show when company sync is configured (deal visibility is enforced by the
  // page + backend filters) or, as a fallback, when the user has personal
  // credentials. Hide while either subscription is still loading.
  if (credsLoading || accessLoading || !canUseArive) return null;

  if (dismissed || deal?.ariveId || matches.length === 0) return null;

  if (loading) return null;

  return (
    <Box mb={4}>
      <Alert status="info" borderRadius="lg" variant="left-accent" pr={8} position="relative">
        <AlertIcon />
        <VStack align="stretch" flex={1} spacing={2}>
          <HStack justify="space-between" wrap="wrap">
            <Text fontSize="sm" fontWeight="medium">
              We found {matches.length} Arive loan{matches.length !== 1 ? 's' : ''} matching this borrower's email.
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={expanded ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide' : 'View Matches'}
            </Button>
          </HStack>

          <Collapse in={expanded} animateOpacity>
            <VStack align="stretch" spacing={2} mt={1}>
              {matches.map((m) => (
                <HStack
                  key={m.ariveLoanId}
                  p={3}
                  bg={rowBg}
                  borderWidth="1px"
                  borderColor={rowBorder}
                  borderRadius="md"
                  justify="space-between"
                  wrap="wrap"
                  gap={2}
                >
                  <VStack align="start" spacing={0}>
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="bold">{m.ariveLoanId}</Text>
                      <Badge colorScheme={getAriveStageColor(m.currentStage)} fontSize="10px">
                        {getAriveStageLabel(m.currentStage)}
                      </Badge>
                      <Badge fontSize="10px" variant="outline">{m.entityType}</Badge>
                    </HStack>
                    <HStack spacing={3} fontSize="xs" color="gray.500">
                      {m.borrowerName && <Text>{m.borrowerName}</Text>}
                      {m.baseLoanAmount > 0 && <Text>${Number(m.baseLoanAmount).toLocaleString()}</Text>}
                      {m.lenderName && <Text>{m.lenderName}</Text>}
                    </HStack>
                  </VStack>
                  <Button
                    size="xs"
                    colorScheme="teal"
                    leftIcon={<FiLink />}
                    onClick={() => handleLink(m.ariveLoanId)}
                    isLoading={linking === m.ariveLoanId}
                    isDisabled={!!linking}
                  >
                    Link
                  </Button>
                </HStack>
              ))}
            </VStack>
          </Collapse>
        </VStack>

        <CloseButton
          position="absolute"
          right={2}
          top={2}
          size="sm"
          onClick={() => setDismissed(true)}
        />
      </Alert>
    </Box>
  );
}
